/** @format */

import PropTypes from "prop-types";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

// material-ui
import {
	// createTheme,
	Grid,
	MenuItem,
	PaletteColorOptions,
	TextField,
	Typography,
	Chip,
} from "@mui/material";

// third-party
import Chart from "react-apexcharts";

// project imports
import SkeletonTotalContributionGrowthBarChart from "../cards/skeleton/SkeletonTotalContributionGrowthBarChart";
import MainCard from "../cards/MainCard";
import { gridSpacing } from "../../utility/generalUtils";
import { AppDispatch } from "../../redux/store/store";

// chart data
import { colors } from "../../utility/types/constant";
import { formatCurrency } from "../../utility/currencyUtils";

// Redux actions and selectors
import { getContributionChartData } from "../../redux/actions/contributionAction";
import { ensureStateStructure } from "../../redux/reducers/contributionReducer";
import {
	selectCombinedChartData,
	selectRealTimeContributionUpdates,
} from "../../redux/selectors/contributionSelector";

export interface TotalContributionGrowthBarChartProps {
	isLoading: boolean;
	circleName: string;
	currentCycleTotalContribution: string;
	unionId: string;
	cycleId?: string;
	enableRealTime?: boolean;
	currency?: string;
}

declare module "@mui/material/styles" {
	interface PaletteColor {
		800?: string;
	}
	interface SimplePaletteColorOptions {
		800?: string;
	}

	interface TypographyVariants {
		commonAvatar: React.CSSProperties;
		smallAvatar: React.CSSProperties;
		mediumAvatar: React.CSSProperties;
		largeAvatar: React.CSSProperties;
	}

	// Allow configuration using `createTheme`
	interface TypographyVariantsOptions {
		commonAvatar?: React.CSSProperties;
		smallAvatar?: React.CSSProperties;
		mediumAvatar: React.CSSProperties;

		largeAvatar?: React.CSSProperties;
	}

	interface Palette {
		dark: PaletteColor;
	}
	interface PaletteOptions {
		dark?: PaletteColorOptions;
	}
}

const status = [
	{
		value: "today",
		label: "Today",
	},
	{
		value: "month",
		label: "This Month",
	},
	{
		value: "year",
		label: "This Year",
	},
];

const TotalContributionGrowthBarChart = (
	props: TotalContributionGrowthBarChartProps,
) => {
	const {
		isLoading,
		circleName,
		currentCycleTotalContribution,
		unionId,
		cycleId,
		enableRealTime = true,
		currency = "usd",
	} = props;

	const dispatch = useDispatch<AppDispatch>();
	const [value, setValue] = useState("year");

	// Real-time selectors - only call if unionId exists
	const chartData = useSelector((state: any) =>
		unionId ? selectCombinedChartData(state, unionId) : null,
	);
	const realTimeUpdates = useSelector((state: any) =>
		unionId ? selectRealTimeContributionUpdates(state, unionId) : null,
	);

	// Enhanced chart data with real-time capabilities
	const enhancedChartData = useMemo(() => {
		if (!chartData?.data) {
			// Return default chart data structure based on period
			const getDefaultDataForPeriod = () => {
				if (value === "year") {
					return {
						series: [
							{
								name: "Monthly Contributions ($)",
								data: new Array(12).fill(0),
							},
						],
						categories: [
							"Jan",
							"Feb",
							"Mar",
							"Apr",
							"May",
							"Jun",
							"Jul",
							"Aug",
							"Sep",
							"Oct",
							"Nov",
							"Dec",
						],
					};
				} else if (value === "month") {
					const daysInMonth = new Date(
						new Date().getFullYear(),
						new Date().getMonth() + 1,
						0,
					).getDate();
					return {
						series: [
							{
								name: "Daily Contributions ($)",
								data: new Array(daysInMonth).fill(0),
							},
						],
						categories: Array.from(
							{ length: daysInMonth },
							(_, i) => `${i + 1}`,
						),
					};
				} else {
					return {
						series: [
							{ name: "Hourly Contributions ($)", data: new Array(24).fill(0) },
						],
						categories: Array.from({ length: 24 }, (_, i) => `${i}:00`),
					};
				}
			};

			const defaultData = getDefaultDataForPeriod();
			return {
				...defaultData,
				hasData: false,
			};
		}

		let series = chartData.data.series || [];
		let categories = chartData.data.categories || [];

		// Apply real-time updates if available
		if (realTimeUpdates?.stats && chartData.period === "year") {
			const monthlyStats = realTimeUpdates.stats.monthlyStats || [];

			const updatedData =
				series.length > 0 ? [...series[0].data] : new Array(12).fill(0);

			monthlyStats.forEach((stat: any) => {
				if (stat._id?.month && stat._id.month >= 1 && stat._id.month <= 12) {
					// Ensure the contribution amount is properly formatted as a number
					const amount = parseFloat(stat.totalAmount) || 0;
					updatedData[stat._id.month - 1] = amount;
				}
			});

			series = [
				{
					name: series[0]?.name || "Monthly Contributions ($)",
					data: updatedData,
				},
			];
		} else {
		}

		// Validate and sanitize series data for ApexCharts
		const sanitizedSeries =
			Array.isArray(series) ?
				series.map((serie) => ({
					name: serie?.name || "Contributions ($)",
					data:
						Array.isArray(serie?.data) ?
							serie.data.map((val: any) => {
								const numVal = typeof val === "number" ? val : parseFloat(val);
								return isNaN(numVal) ? 0 : numVal;
							})
							: [],
				}))
				: [
					{
						name: "Contributions ($)",
						data: [],
					},
				];

		// Validate categories
		const sanitizedCategories =
			Array.isArray(categories) ?
				categories.map((cat: any) => String(cat || ""))
				: [];

		// Check if there's any actual contribution data (not all zeros)
		const hasActualContributions = sanitizedSeries.some((serie) =>
			serie.data.some((val: number) => val > 0),
		);

		const finalResult = {
			series: sanitizedSeries,
			categories: sanitizedCategories,
			hasData:
				sanitizedSeries.length > 0 &&
				sanitizedSeries[0].data.length > 0 &&
				hasActualContributions,
			lastUpdate: realTimeUpdates?.timeStamp || chartData.lastUpdated,
		};

		return finalResult;
	}, [chartData, realTimeUpdates, value, unionId]);

	// Fetch initial chart data
	useEffect(() => {
		// Ensure state structure integrity before doing anything else
		dispatch(ensureStateStructure());

		if (unionId && !isLoading) {
			const params: any = {
				unionId,
				period: value as any,
			};
			if (cycleId) {
				params.cycleId = cycleId;
			}

			dispatch(getContributionChartData(params));
		} else {
		}
	}, [dispatch, unionId, value, cycleId, isLoading]);

	// Handle period changes
	const handlePeriodChange = useCallback(
		(newPeriod: string) => {
			setValue(newPeriod);
			if (unionId) {
				const params: any = {
					unionId,
					period: newPeriod as any,
				};
				if (cycleId) {
					params.cycleId = cycleId;
				}
				dispatch(getContributionChartData(params));
			}
		},
		[dispatch, unionId, cycleId],
	);

	// Generate chart options with useMemo to avoid circular dependencies
	const chartOptions = useMemo(() => {
		if (
			!enhancedChartData.categories ||
			enhancedChartData.categories.length === 0 ||
			!enhancedChartData.series ||
			enhancedChartData.series.length === 0
		) {
			return null;
		}

		// Chart configuration with custom colors
		return {
			chart: {
				type: "bar" as const,
				height: 480,
				toolbar: {
					show: false,
				},
			},
			colors: [colors.mainSecondary],
			plotOptions: {
				bar: {
					borderRadius: 4,
					columnWidth: "60%",
				},
			},
			dataLabels: {
				enabled: false,
			},
			xaxis: {
				categories: enhancedChartData.categories,
				axisBorder: {
					show: false,
				},
				axisTicks: {
					show: false,
				},
			},
			yaxis: {
				title: {
					text: "Contribution Amount ($)",
				},
			},
			grid: {
				borderColor: "#f1f1f1",
				strokeDashArray: 4,
			},
			tooltip: {
				y: {
					formatter: (value: number) => formatCurrency(value, currency),
				},
			},
		};
	}, [enhancedChartData]);

	// Early validation for required props
	if (!unionId) {
		return (
			<MainCard>
				<Typography
					variant="h6"
					color="error"
					textAlign="center"
					sx={{ py: 4 }}>
					Union ID is required to display contribution chart
				</Typography>
			</MainCard>
		);
	}

	return (
		<>
			{isLoading ?
				<SkeletonTotalContributionGrowthBarChart />
				: <MainCard>
					<Grid container spacing={gridSpacing}>
						<Grid item xs={12}>
							<Grid
								container
								alignItems="center"
								justifyContent="space-between">
								<Grid item>
									<Grid container direction="column" spacing={1}>
										<Grid item>
											<Typography
												variant="subtitle2"
												sx={{ fontSize: "1.2rem" }}>
												{circleName} Total Growth Contribution
												{enableRealTime && realTimeUpdates && (
													<Chip
														label="Live Updates"
														size="small"
														color="success"
														sx={{ ml: 1 }}
													/>
												)}
											</Typography>
										</Grid>
										<Grid item>
											<Typography
												variant="h3"
												sx={{ color: colors.mainPrimary }}>
												{formatCurrency(Number(currentCycleTotalContribution), currency)}
											</Typography>
											{realTimeUpdates?.newContribution && (
												<Typography variant="caption" color="success.main">
													+{formatCurrency(realTimeUpdates.newContribution.amount, currency)} just now
												</Typography>
											)}
										</Grid>
									</Grid>
								</Grid>
								<Grid item>
									<TextField
										id="period-select"
										select
										value={value}
										onChange={(e) => handlePeriodChange(e.target.value)}
										size="small">
										{status.map((option) => (
											<MenuItem key={option.value} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</TextField>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							{(
								enhancedChartData.hasData &&
								chartOptions &&
								enhancedChartData.series &&
								enhancedChartData.series.length > 0 &&
								enhancedChartData.categories &&
								enhancedChartData.categories.length > 0
							) ?
								(() => {
									try {
										return (
											<Chart
												key={`chart-${unionId}-${value}-${realTimeUpdates?.timeStamp || "static"
													}`}
												options={chartOptions}
												series={enhancedChartData.series}
												type="bar"
												height={480}
											/>
										);
									} catch (error) {
										return (
											<Typography color="error" variant="body2">
												Chart rendering error:{" "}
												{error instanceof Error ?
													error.message
													: "Unknown error"}
											</Typography>
										);
									}
								})()
								: <Typography
									variant="h6"
									textAlign="center"
									color="text.secondary"
									sx={{ py: 4 }}>
									{isLoading ?
										"Loading contribution data..."
										: `No contribution data available for ${circleName || "this circle"
										}`
									}
								</Typography>
							}
						</Grid>
					</Grid>
				</MainCard>
			}
		</>
	);
};

TotalContributionGrowthBarChart.propTypes = {
	isLoading: PropTypes.bool,
	circleName: PropTypes.string,
	currentCycleTotalContribution: PropTypes.string,
	unionId: PropTypes.string.isRequired,
	cycleId: PropTypes.string,
	enableRealTime: PropTypes.bool,
};

export default TotalContributionGrowthBarChart;
